/**
 * @fileoverview Unified Survey Storage (New Architecture)
 * @description Manages survey data using StorageService with SQL/localStorage adapters
 *
 * Features:
 * - SQL storage via BFF (localStorage only as a transient pre-auth fallback)
 * - Version tracking for optimistic locking
 * - Backward compatible API with legacy UnifiedStorage
 * - Supports both individual survey operations and bulk operations
 *
 * @example
 * ```ts
 * import * as SurveyStorage from '@/services/survey-storage.service';
 *
 * // Initialize survey
 * const survey = await SurveyStorage.initializeSurvey({
 *   surveyId: 'survey-123',
 *   surveyName: 'My Survey'
 * });
 *
 * // Update assumptions
 * await SurveyStorage.updateAssumptions('survey-123', assumptions);
 * ```
 */

import { StorageService } from './storage-service'
import { createStorageAdapter } from './storage/factory'
import type { AuthService } from './storage/factory'
import type { SurveyData, InitializeSurveyOptions } from '@/types/unified-storage.types'
import type { SurveyAssumptions } from '@/types/assumptions.types'
import type { SurveyPortMapping, PortMappingTemplate } from '@/types/portMapping.types'
import type { SurveyMasterEquipmentSettings } from '@/types/master-equipment.types'

const STORAGE_VERSION = 1

// Entity-specific storage services
let assumptionsService: StorageService<SurveyAssumptions> | null = null
let portMappingService: StorageService<SurveyPortMapping> | null = null
let masterEquipmentService: StorageService<SurveyMasterEquipmentSettings> | null = null
let activeSurveyId: string | null = null

/**
 * Initialize entity-specific storage services with adapter
 * @param authService Optional Auth0 service for SQL storage
 * @param force Force re-initialization even if already initialized
 */
export function initializeStorage(authService?: AuthService, force = false): void {
  if (assumptionsService && portMappingService && masterEquipmentService && !force) {
    return // Already initialized
  }

  // Create entity-specific adapters
  const assumptionsAdapter = createStorageAdapter<SurveyAssumptions>('assumptions', authService)
  const portMappingAdapter = createStorageAdapter<SurveyPortMapping>('port-mappings', authService)
  const masterEquipmentAdapter = createStorageAdapter<SurveyMasterEquipmentSettings>(
    'master-equipment',
    authService
  )

  // Create entity-specific services
  assumptionsService = new StorageService<SurveyAssumptions>(assumptionsAdapter)
  portMappingService = new StorageService<SurveyPortMapping>(portMappingAdapter)
  masterEquipmentService = new StorageService<SurveyMasterEquipmentSettings>(masterEquipmentAdapter)
}

/**
 * Get entity-specific storage services (lazy initialization)
 */
function getStorageServices(authService?: AuthService): {
  assumptions: StorageService<SurveyAssumptions>
  portMapping: StorageService<SurveyPortMapping>
  masterEquipment: StorageService<SurveyMasterEquipmentSettings>
} {
  if (!assumptionsService || !portMappingService || !masterEquipmentService) {
    // SQL storage is the only backend; before auth is ready, fall back to localStorage.
    if (!authService) {
      // Force localStorage as fallback
      const assumptionsAdapter = createStorageAdapter<SurveyAssumptions>('assumptions', undefined, {
        forceType: 'local',
      })
      const portMappingAdapter = createStorageAdapter<SurveyPortMapping>(
        'port-mappings',
        undefined,
        { forceType: 'local' }
      )
      const masterEquipmentAdapter = createStorageAdapter<SurveyMasterEquipmentSettings>(
        'master-equipment',
        undefined,
        { forceType: 'local' }
      )

      assumptionsService = new StorageService<SurveyAssumptions>(assumptionsAdapter)
      portMappingService = new StorageService<SurveyPortMapping>(portMappingAdapter)
      masterEquipmentService = new StorageService<SurveyMasterEquipmentSettings>(
        masterEquipmentAdapter
      )
    } else {
      initializeStorage(authService)
    }
  }

  return {
    assumptions: assumptionsService!,
    portMapping: portMappingService!,
    masterEquipment: masterEquipmentService!,
  }
}

/**
 * Create empty survey data structure
 */
function createEmptySurveyData(surveyId: string, surveyName: string): SurveyData {
  const now = new Date().toISOString()

  const assumptions: SurveyAssumptions = {
    surveyId,
    surveyName,
    equipmentPortCounts: {},
    createdAt: now,
    updatedAt: now,
    version: 1,
  }

  const defaultTemplate: PortMappingTemplate = {
    id: 'default',
    name: 'Default (1-to-1)',
    description: 'Maps port 1 to port 1, port 2 to port 2, etc.',
    rules: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  }

  const portMapping: SurveyPortMapping = {
    surveyId,
    activeTemplateId: 'default',
    templates: [defaultTemplate],
  }

  const masterEquipment: SurveyMasterEquipmentSettings = {
    surveyId,
    surveyName,
    equipmentConfigs: {},
    createdAt: now,
    updatedAt: now,
    version: 1,
  }

  return {
    surveyId,
    surveyName,
    assumptions,
    portMapping,
    masterEquipment,
    createdAt: now,
    updatedAt: now,
    version: STORAGE_VERSION,
  }
}

/**
 * Initialize or get survey data by loading all entity-specific data
 */
export async function initializeSurvey(options: InitializeSurveyOptions): Promise<SurveyData> {
  const services = getStorageServices()

  // Try to load existing entity data in parallel
  const [assumptionsResult, portMappingResult, masterEquipmentResult] = await Promise.all([
    services.assumptions.get(options.surveyId),
    services.portMapping.get(options.surveyId),
    services.masterEquipment.get(options.surveyId),
  ])

  // Check if any data exists
  const hasExistingData =
    (assumptionsResult.success && assumptionsResult.data) ||
    (portMappingResult.success && portMappingResult.data) ||
    (masterEquipmentResult.success && masterEquipmentResult.data)

  if (hasExistingData) {
    // Construct survey data from existing entities
    const survey = createEmptySurveyData(options.surveyId, options.surveyName)

    if (assumptionsResult.success && assumptionsResult.data) {
      survey.assumptions = assumptionsResult.data.data
    }
    if (portMappingResult.success && portMappingResult.data) {
      survey.portMapping = portMappingResult.data.data
    }
    if (masterEquipmentResult.success && masterEquipmentResult.data) {
      survey.masterEquipment = masterEquipmentResult.data.data
    }

    activeSurveyId = options.surveyId
    return survey
  }

  // Create new survey with all entities
  const newSurvey = createEmptySurveyData(options.surveyId, options.surveyName)

  // Save all entities in parallel
  const [assumptionsSave, portMappingSave, masterEquipmentSave] = await Promise.all([
    services.assumptions.save(options.surveyId, newSurvey.assumptions),
    services.portMapping.save(options.surveyId, newSurvey.portMapping),
    services.masterEquipment.save(options.surveyId, newSurvey.masterEquipment),
  ])

  // Check for save errors
  if (!assumptionsSave.success) {
    throw new Error(`Failed to save assumptions: ${assumptionsSave.error?.message}`)
  }
  if (!portMappingSave.success) {
    throw new Error(`Failed to save port mapping: ${portMappingSave.error?.message}`)
  }
  if (!masterEquipmentSave.success) {
    throw new Error(`Failed to save master equipment: ${masterEquipmentSave.error?.message}`)
  }

  activeSurveyId = options.surveyId
  return newSurvey
}

/**
 * Get survey data by ID (loads all entities)
 */
export async function getSurveyData(surveyId: string): Promise<SurveyData | null> {
  const services = getStorageServices()

  // Load all entity data in parallel
  const [assumptionsResult, portMappingResult, masterEquipmentResult] = await Promise.all([
    services.assumptions.get(surveyId),
    services.portMapping.get(surveyId),
    services.masterEquipment.get(surveyId),
  ])

  // Check if any data exists
  const hasData =
    (assumptionsResult.success && assumptionsResult.data) ||
    (portMappingResult.success && portMappingResult.data) ||
    (masterEquipmentResult.success && masterEquipmentResult.data)

  if (!hasData) {
    return null
  }

  // Construct survey data from entities
  const survey = createEmptySurveyData(surveyId, surveyId)

  if (assumptionsResult.success && assumptionsResult.data?.data) {
    survey.assumptions = assumptionsResult.data.data
    survey.surveyName = assumptionsResult.data.data.surveyName
  }
  if (portMappingResult.success && portMappingResult.data?.data) {
    survey.portMapping = portMappingResult.data.data
  }
  if (masterEquipmentResult.success && masterEquipmentResult.data?.data) {
    survey.masterEquipment = masterEquipmentResult.data.data
  }

  return survey
}

/**
 * Get active survey ID
 */
export function getActiveSurveyId(): string | null {
  return activeSurveyId
}

/**
 * Update survey assumptions (entity-specific)
 */
export async function updateSurveyAssumptions(
  surveyId: string,
  assumptions: SurveyAssumptions
): Promise<SurveyAssumptions> {
  const services = getStorageServices()
  // Pass the current version for optimistic locking
  const result = await services.assumptions.save(surveyId, assumptions, {
    expectedVersion: assumptions.version,
  })

  if (!result.success || !result.data) {
    throw new Error(`Failed to update assumptions: ${result.error?.message}`)
  }

  // Return the updated data with new version from the response
  return {
    ...result.data.data,
    version: result.data.metadata.version,
  }
}

/**
 * Update survey port mapping settings (entity-specific)
 */
export async function updateSurveyPortMapping(
  surveyId: string,
  portMapping: SurveyPortMapping
): Promise<void> {
  const services = getStorageServices()

  // Use surveyId as surveyName fallback - the BFF will lookup the actual name if needed
  const surveyName = surveyId

  // Add surveyName to data for the BFF service
  const dataWithSurveyName = {
    ...portMapping,
    surveyName,
  }

  const result = await services.portMapping.save(surveyId, dataWithSurveyName)

  if (!result.success) {
    throw new Error(`Failed to update port mapping: ${result.error?.message}`)
  }
}

/**
 * Update survey master equipment settings (entity-specific)
 */
export async function updateSurveyMasterEquipment(
  surveyId: string,
  masterEquipment: SurveyMasterEquipmentSettings
): Promise<void> {
  const services = getStorageServices()

  // Use surveyId as surveyName fallback - the BFF will lookup the actual name if needed
  const surveyName = masterEquipment.surveyName || surveyId

  // Add surveyName to data for the BFF service
  const dataWithSurveyName = {
    ...masterEquipment,
    surveyName,
  }

  const result = await services.masterEquipment.save(surveyId, dataWithSurveyName)

  if (!result.success) {
    throw new Error(`Failed to update master equipment: ${result.error?.message}`)
  }
}

/**
 * Get all surveys
 */
export async function getAllSurveys(): Promise<Record<string, SurveyData>> {
  const services = getStorageServices()

  // List all survey IDs from assumptions (primary entity)
  const listResult = await services.assumptions.list()

  if (!listResult.success) {
    return {}
  }

  const surveys: Record<string, SurveyData> = {}
  const metadataList = listResult.data as unknown as Array<{ id: string }>

  // Load each survey
  for (const metadata of metadataList) {
    const surveyId = metadata.id
    const surveyData = await getSurveyData(surveyId)
    if (surveyData) {
      // Safe: surveyId comes from our own storage system
      // eslint-disable-next-line security/detect-object-injection
      surveys[surveyId] = surveyData
    }
  }

  return surveys
}

/**
 * Delete a survey (deletes all entities)
 */
export async function deleteSurvey(surveyId: string): Promise<void> {
  const services = getStorageServices()

  // Delete all entities in parallel
  const [assumptionsResult, portMappingResult, masterEquipmentResult] = await Promise.all([
    services.assumptions.delete(surveyId),
    services.portMapping.delete(surveyId),
    services.masterEquipment.delete(surveyId),
  ])

  // Report any errors (but don't fail if entity doesn't exist)
  if (!assumptionsResult.success) {
    console.warn(`Failed to delete assumptions: ${assumptionsResult.error?.message}`)
  }
  if (!portMappingResult.success) {
    console.warn(`Failed to delete port mapping: ${portMappingResult.error?.message}`)
  }
  if (!masterEquipmentResult.success) {
    console.warn(`Failed to delete master equipment: ${masterEquipmentResult.error?.message}`)
  }

  if (activeSurveyId === surveyId) {
    activeSurveyId = null
  }
}

/**
 * Clear all surveys (use with caution)
 */
export async function clearAllData(): Promise<void> {
  const surveys = await getAllSurveys()

  for (const surveyId of Object.keys(surveys)) {
    await deleteSurvey(surveyId)
  }

  activeSurveyId = null
}
